import { useCallback, useSyncExternalStore } from 'react';
import {
  AWSService,
  ContentType,
  Environment,
  TechDomain,
  TechDomainDisplayNames,
  UserType,
} from '@amzn/awscat-pattern-library-service-model';
import { useHistory } from 'react-router-dom';

import { getAuthorsByline, getVersionLabel, LoadedDocument } from '../../model/LoadedDocument';
import { awsServiceDisplayNames, environmentDisplayNames } from '../utils/DisplayNames';
import { useContentEditorService } from '../../contexts/ContentEditorServiceContext';
import { displayAuthorInfo, extractUserAlias, getUserTypeFromId } from '../../api/UtilsApi';
import {
  ContentAttributions,
  ContentAttributionUser,
  ContentEditorService,
} from '../../services/ContentEditorService';
import { useLoadedDocumentHandle } from '../v2-hooks/useLoadedDocumentHandle';
import { TabId } from '../v2-hooks/useUrlHash';

/** Tab id of the History tab in the read-only view; the "previous versions" link targets it. */
const HISTORY_TAB_ID: TabId = 'history';

/** Read-only presentation state for the content metadata table. */
export interface ContentMetadataViewModel {
  /** Content-type-aware section heading. */
  readonly heading: string;
  /** "Owner and co-authors" string; the Authoring row value. */
  readonly authoring: string;
  /** Assigned content bar raiser, or `undefined` when none. */
  readonly contentBarRaisers: string | undefined;
  /** Assigned technical writer, or `undefined` when none. */
  readonly technicalWriting: string | undefined;
  /** Display label for the main technology, or `undefined` when unset. */
  readonly mainTechnology: string | undefined;
  /** Comma-separated additional technology labels, or `undefined` when none. */
  readonly additionalTechnologies: string | undefined;
  /** Comma-separated AWS service labels, or `undefined` when none. */
  readonly awsServices: string | undefined;
  /** Display label for the target environment, or `undefined` when unset. */
  readonly environment: string | undefined;
  /** Version label (e.g. `"2.0"`), or `undefined` when unknown. */
  readonly version: string | undefined;
  /** Opens the History tab, where prior published versions are listed. */
  readonly viewPreviousVersions: () => void;
}

/** Reads the template-metadata bag off the document; typed `any` in the service model. */
function getTemplateMetadata(document: LoadedDocument): Record<string, unknown> {
  const bag = document.content.templateMetadata as unknown;
  return bag && typeof bag === 'object' ? (bag as Record<string, unknown>) : {};
}

/** Builds the section heading for each content type supported by the read-only viewer. */
function getHeading(contentType: ContentType): string {
  switch (contentType) {
    case ContentType.Pattern:
      return 'About this pattern';
    case ContentType.Guide:
      return 'About this guide';
    case ContentType.Strategy:
      return 'About this strategy';
    default:
      return 'About this content';
  }
}

/** Maps a raw AWS service code to its display name, falling back to the raw code. */
function toAwsServiceLabel(code: string): string {
  return Object.values(AWSService).includes(code as AWSService)
    ? (awsServiceDisplayNames.get(code as AWSService) ?? code)
    : code;
}

/** Maps a raw tech-domain code to its display name, falling back to the raw code. */
function toTechDomainLabel(code: string): string {
  return Object.values(TechDomain).includes(code as TechDomain)
    ? (TechDomainDisplayNames.get(code as TechDomain) ?? code)
    : code;
}

/** Joins string-array metadata into a comma-separated label, or `undefined` when empty. */
function joinLabels(values: unknown, toLabel: (code: string) => string): string | undefined {
  if (!Array.isArray(values) || values.length === 0) return undefined;
  return values
    .filter((v): v is string => typeof v === 'string')
    .map(toLabel)
    .join(', ');
}

/** Subscribes to attribution updates loaded in the background by the editor service. */
function useContentAttributions(service: ContentEditorService): ContentAttributions | undefined {
  // When the service emits 'changed', then re-read the attributions snapshot
  // so the table updates as the background load completes.
  const subscribe = useCallback(
    (onStoreChange: () => void) => service.on('changed', onStoreChange),
    [service],
  );
  return useSyncExternalStore(subscribe, () => service.loadedDocumentHandle?.attributions);
}

/** Formats a resolved assignee, falling back to their alias when profile lookup failed. */
function formatAssignedUser(user: ContentAttributionUser | undefined): string | undefined {
  if (!user) return undefined;
  const organization = getUserTypeFromId(user.userId) === UserType.AWS ? 'AWS' : undefined;
  return displayAuthorInfo(user.userName || extractUserAlias(user.userId), organization);
}

/**
 * Projects the loaded document into the content metadata table: authoring,
 * review assignments, technologies, AWS services, environment, and version.
 * Returns undefined while no document handle is loaded — the view renders
 * nothing during that transient state rather than crashing.
 */
export function useContentMetadataViewModel(): ContentMetadataViewModel | undefined {
  const service = useContentEditorService();
  const history = useHistory();
  const attributions = useContentAttributions(service);
  const handle = useLoadedDocumentHandle();

  // When the link is followed, then switch to the History tab by setting the
  // URL hash the read-only view keys its active tab on.
  const viewPreviousVersions = useCallback(() => {
    history.replace({ hash: HISTORY_TAB_ID });
  }, [history]);

  // A parent-teardown render can transiently clear the handle before this
  // view unmounts; return undefined so the view renders null instead of throwing.
  if (!handle) return undefined;

  const { document } = handle;
  const templateMetadata = getTemplateMetadata(document);

  const mainTechDomain = templateMetadata.mainTechDomain;
  const environment = templateMetadata.environment;

  return {
    heading: getHeading(document.content.contentMetadata.contentType),
    authoring: getAuthorsByline(document),
    contentBarRaisers: formatAssignedUser(attributions?.contentBarRaiser),
    technicalWriting: formatAssignedUser(attributions?.technicalWriter),
    mainTechnology:
      typeof mainTechDomain === 'string' ? toTechDomainLabel(mainTechDomain) : undefined,
    additionalTechnologies: joinLabels(templateMetadata.techDomains, toTechDomainLabel),
    awsServices: joinLabels(templateMetadata.awsServices, toAwsServiceLabel),
    environment:
      typeof environment === 'string'
        ? (environmentDisplayNames.get(environment as Environment) ?? environment)
        : undefined,
    version:
      handle.options.kind === 'read-publication' && handle.options.workflowId !== undefined
        ? `${handle.options.workflowId}.0`
        : getVersionLabel(document),
    viewPreviousVersions,
  };
}
